import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, CalendarRange, Infinity as InfinityIcon } from "lucide-react";

function PlanCard({ p, delay }) {
  const pct = p.days_total ? Math.round((p.completed_count / p.days_total) * 100) : 0;
  return (
    <Link to={`/plans/${p.id}`} data-testid={`plan-card-${p.id}`}
      className="group bg-card border border-border rounded-md p-8 hover:bg-muted/40 transition-colors duration-300 animate-fade-up flex flex-col"
      style={{ animationDelay: `${delay}ms` }}>
      <p className="label-overline mb-3">{p.tagline}</p>
      <h3 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight">{p.name}</h3>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{p.description}</p>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-5">
        {p.self_paced ? <InfinityIcon className="w-3.5 h-3.5 text-accent" /> : <CalendarRange className="w-3.5 h-3.5 text-accent" />}
        {p.self_paced ? "Self-paced" : `${p.days_total} days`} · {p.readings_total} chapters
      </div>
      {p.started ? (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
            <span data-testid={`plan-progress-${p.id}`}>{p.self_paced ? `Chapter ${p.current_day}` : `Day ${p.current_day}`} · {pct}%</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 group-hover:text-primary transition-all duration-300" />
          </div>
          <div className="h-1 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
        </div>
      ) : (
        <span className="flex items-center gap-2 text-sm text-primary mt-4 group-hover:text-accent transition-colors">
          Begin this plan <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
        </span>
      )}
    </Link>
  );
}

export default function ReadingPlans() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <div className="animate-fade-up">
        <p className="label-overline mb-2">Structured Journeys</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none">Reading Plans</h1>
        <p className="text-muted-foreground mt-4 max-w-xl leading-relaxed">
          Follow a guided path through Scripture — a daily portion, a steady rhythm, and a heart shaped by the Word.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mt-12" data-testid="plans-grid">
        {plans.map((p, i) => <PlanCard key={p.id} p={p} delay={i * 60} />)}
      </div>
    </div>
  );
}
